/**
 * 🎠 CAROUSEL MANAGEMENT PAGE
 * Admin interface to manage dashboard carousels
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Tooltip,
  Paper,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  ViewCarousel,
  Visibility,
  VisibilityOff,
  Code,
  FilterList,
} from '@mui/icons-material';
import { carouselService, type Carousel, type FilterCriteria } from '@/services/carouselService';
import toast from 'react-hot-toast';

export const CarouselManagementPage: React.FC = () => {
  const [carousels, setCarousels] = useState<Carousel[]>([]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [editingCarousel, setEditingCarousel] = useState<Carousel | null>(null);
  const [deletingCarousel, setDeletingCarousel] = useState<Carousel | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    max_vehicles: 10,
    display_order: 1,
    card_style: 'default',
    is_active: true,
  });

  // Filter builder state
  const [filters, setFilters] = useState<{
    location?: string;
    make?: string;
    category?: string;
    transmission?: string;
    yearOperator?: string;
    yearValue?: number;
    rateOperator?: string;
    rateValue?: number;
  }>({});

  useEffect(() => {
    loadCarousels();
  }, []);

  const loadCarousels = async () => {
    try {
      setLoading(true);
      setApiError(null);
      const result = await carouselService.getCarousels();
      if (result.success) {
        const allCarousels = result.data.carousels || [];

        setCarousels(allCarousels);
      } else {
        setApiError(result.error || 'Failed to load carousels');
      }
    } catch (error) {
      setApiError('Unable to connect to carousel service');
      console.error('Error loading carousels:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (carousel?: Carousel) => {
    if (carousel) {
      setEditingCarousel(carousel);
      setFormData({
        title: carousel.title,
        subtitle: carousel.subtitle || '',
        max_vehicles: carousel.max_vehicles,
        display_order: carousel.display_order,
        card_style: carousel.card_style,
        is_active: carousel.is_active,
      });

      // Parse existing filters
      const criteria = carousel.filter_criteria;
      setFilters({
        location: criteria.location,
        make: criteria.make,
        category: criteria.category,
        transmission: criteria.transmission,
        yearOperator: criteria.year?.operator,
        yearValue: criteria.year?.value,
        rateOperator: criteria.daily_rate?.operator,
        rateValue: criteria.daily_rate?.value,
      });
    } else {
      setEditingCarousel(null);
      setFormData({
        title: '',
        subtitle: '',
        max_vehicles: 10,
        display_order: (carousels.length + 1),
        card_style: 'default',
        is_active: true,
      });
      setFilters({});
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCarousel(null);
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);

      // Build filter criteria from form fields
      const filterCriteria: FilterCriteria = {};

      if (filters.location) filterCriteria.location = filters.location;
      if (filters.make) filterCriteria.make = filters.make;
      if (filters.category) filterCriteria.category = filters.category;
      if (filters.transmission) filterCriteria.transmission = filters.transmission;

      if (filters.yearOperator && filters.yearValue) {
        filterCriteria.year = {
          operator: filters.yearOperator as any,
          value: filters.yearValue
        };
      }

      if (filters.rateOperator && filters.rateValue) {
        filterCriteria.daily_rate = {
          operator: filters.rateOperator as any,
          value: filters.rateValue
        };
      }

      const data = {
        title: formData.title,
        subtitle: formData.subtitle || undefined,
        filterCriteria: filterCriteria,
        maxVehicles: formData.max_vehicles,
        displayOrder: formData.display_order,
        cardStyle: formData.card_style,
        isActive: formData.is_active,
      };


      let result;
      if (editingCarousel) {
        result = await carouselService.updateCarousel(editingCarousel.id, data);
      } else {
        result = await carouselService.createCarousel(data);
      }

      if (result.success) {
        toast.success(editingCarousel ? 'Carousel updated' : 'Carousel created');
        handleCloseDialog();
        loadCarousels();
      } else {
        toast.error(result.error || 'Failed to save carousel');
      }
    } catch (error) {
      console.error('Error saving carousel:', error);
      toast.error('Failed to save carousel');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingCarousel) return;

    try {
      setSubmitting(true);
      const result = await carouselService.deleteCarousel(deletingCarousel.id);

      if (result.success) {
        toast.success('Carousel deleted');
        setOpenDeleteDialog(false);
        setDeletingCarousel(null);
        loadCarousels();
      } else {
        toast.error(result.error || 'Failed to delete carousel');
      }
    } catch (error) {
      console.error('Error deleting carousel:', error);
      toast.error('Failed to delete carousel');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (carousel: Carousel) => {
    try {
      const newActiveState = !carousel.is_active;

      const result = await carouselService.updateCarousel(carousel.id, {
        isActive: newActiveState
      });

      if (result.success) {
        const message = newActiveState
          ? `"${carousel.title}" is now visible on the app`
          : `"${carousel.title}" is now hidden from the app (not deleted)`;
        toast.success(message);
        loadCarousels();
      } else {
        toast.error(result.error || 'Failed to update carousel');
      }
    } catch (error) {
      console.error('Error toggling carousel:', error);
      toast.error('Failed to update carousel');
    }
  };

  const formatFilters = (filters: FilterCriteria): string => {
    const parts: string[] = [];

    if (filters.location) parts.push(`Location: ${filters.location}`);
    if (filters.make) parts.push(`Make: ${filters.make}`);
    if (filters.category) parts.push(`Category: ${filters.category}`);
    if (filters.transmission) parts.push(`Transmission: ${filters.transmission}`);
    if (filters.year) {
      parts.push(`Year ${filters.year.operator} ${filters.year.value}`);
    }
    if (filters.daily_rate) {
      parts.push(`Rate ${filters.daily_rate.operator} ₦${filters.daily_rate.value.toLocaleString()}`);
    }

    return parts.join(', ') || 'No filters';
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Dashboard Carousels
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage carousel configurations for the renter dashboard
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Create Carousel
        </Button>
      </Box>

      {/* Table */}
      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : apiError ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <ViewCarousel sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Could not load carousels
              </Typography>
              <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
                There was a problem loading the carousel data. Please try again.
              </Typography>
              <Button variant="outlined" onClick={loadCarousels}>
                Retry
              </Button>
            </Box>
          ) : carousels.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <ViewCarousel sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No carousels configured
              </Typography>
              <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
                Create your first carousel to display curated vehicle collections
              </Typography>
              <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
                Create Carousel
              </Button>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Order</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Subtitle</TableCell>
                    <TableCell>Filter Criteria</TableCell>
                    <TableCell align="center">Vehicles Found</TableCell>
                    <TableCell>Max Vehicles</TableCell>
                    <TableCell>Card Style</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Analytics</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {carousels
                    .sort((a, b) => a.display_order - b.display_order)
                    .map((carousel) => (
                    <TableRow key={carousel.id} hover>
                      <TableCell>
                        <Chip label={carousel.display_order} size="small" color="primary" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {carousel.title}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {carousel.subtitle || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={<pre>{JSON.stringify(carousel.filter_criteria, null, 2)}</pre>}>
                          <Typography variant="caption" sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            cursor: 'pointer'
                          }}>
                            <Code fontSize="small" />
                            {formatFilters(carousel.filter_criteria)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={carousel.vehicle_count ?? '-'}
                          size="small"
                          color={
                            carousel.vehicle_count === 0 ? 'error' :
                            (carousel.vehicle_count ?? 0) > 0 ? 'success' :
                            'default'
                          }
                          sx={{ fontWeight: 600, minWidth: 50 }}
                        />
                      </TableCell>
                      <TableCell>{carousel.max_vehicles}</TableCell>
                      <TableCell>
                        <Chip label={carousel.card_style} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={carousel.is_active ? 'Active' : 'Inactive'}
                          size="small"
                          color={carousel.is_active ? 'success' : 'default'}
                          icon={carousel.is_active ? <Visibility /> : <VisibilityOff />}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" display="block">
                          Views: {carousel.total_views}
                        </Typography>
                        <Typography variant="caption" display="block">
                          Clicks: {carousel.total_clicks}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Tooltip title={carousel.is_active ? 'Deactivate' : 'Activate'}>
                            <IconButton
                              size="small"
                              onClick={() => handleToggleActive(carousel)}
                              sx={{
                                color: carousel.is_active ? 'warning.main' : 'success.main',
                                '&:hover': {
                                  backgroundColor: carousel.is_active ? 'warning.light' : 'success.light'
                                }
                              }}
                            >
                              {carousel.is_active ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog(carousel)}
                              sx={{
                                color: 'primary.main',
                                '&:hover': { backgroundColor: 'primary.light' }
                              }}
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setDeletingCarousel(carousel);
                                setOpenDeleteDialog(true);
                              }}
                              sx={{
                                color: 'error.main',
                                '&:hover': { backgroundColor: 'error.light' }
                              }}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCarousel ? 'Edit Carousel' : 'Create Carousel'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              fullWidth
            />

            <TextField
              label="Subtitle"
              value={formData.subtitle}
              onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
              fullWidth
            />

            {/* Filter Builder Section */}
            <Box sx={{ mt: 2, mb: 1 }}>
              <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <FilterList fontSize="small" />
                Vehicle Filters
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Configure which vehicles to show in this carousel. Leave fields empty to skip that filter.
              </Typography>
            </Box>

            {/* Basic Filters */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Location"
                placeholder="e.g., Lagos, Abuja, Port Harcourt"
                value={filters.location || ''}
                onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                fullWidth
                helperText="Filter vehicles by city or location"
              />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Make"
                  placeholder="e.g., Toyota, Honda, Mercedes"
                  value={filters.make || ''}
                  onChange={(e) => setFilters({ ...filters, make: e.target.value })}
                  fullWidth
                  helperText="Filter by vehicle manufacturer"
                />

                <TextField
                  label="Category"
                  placeholder="e.g., sedan, suv, luxury"
                  value={filters.category || ''}
                  onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                  fullWidth
                  helperText="Filter by vehicle category"
                />
              </Box>

              <FormControl fullWidth>
                <InputLabel>Transmission</InputLabel>
                <Select
                  value={filters.transmission || ''}
                  label="Transmission"
                  onChange={(e) => setFilters({ ...filters, transmission: e.target.value })}
                >
                  <MenuItem value="">
                    <em>Any</em>
                  </MenuItem>
                  <MenuItem value="automatic">Automatic</MenuItem>
                  <MenuItem value="manual">Manual</MenuItem>
                </Select>
              </FormControl>

              {/* Year Filter */}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Year Filter (Optional)
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl sx={{ minWidth: 120 }}>
                    <InputLabel>Operator</InputLabel>
                    <Select
                      value={filters.yearOperator || ''}
                      label="Operator"
                      onChange={(e) => setFilters({ ...filters, yearOperator: e.target.value })}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      <MenuItem value=">=">≥ (Greater or Equal)</MenuItem>
                      <MenuItem value=">">{'>'} (Greater Than)</MenuItem>
                      <MenuItem value="<=">≤ (Less or Equal)</MenuItem>
                      <MenuItem value="<">{'<'} (Less Than)</MenuItem>
                      <MenuItem value="=">=  (Equal To)</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="Year"
                    type="number"
                    placeholder="e.g., 2020"
                    value={filters.yearValue || ''}
                    onChange={(e) => setFilters({ ...filters, yearValue: parseInt(e.target.value) || undefined })}
                    fullWidth
                    inputProps={{ min: 1990, max: new Date().getFullYear() + 1 }}
                    disabled={!filters.yearOperator}
                  />
                </Box>
              </Box>

              {/* Daily Rate Filter */}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Daily Rate Filter (Optional)
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl sx={{ minWidth: 120 }}>
                    <InputLabel>Operator</InputLabel>
                    <Select
                      value={filters.rateOperator || ''}
                      label="Operator"
                      onChange={(e) => setFilters({ ...filters, rateOperator: e.target.value })}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      <MenuItem value=">=">≥ (Greater or Equal)</MenuItem>
                      <MenuItem value=">">{'>'} (Greater Than)</MenuItem>
                      <MenuItem value="<=">≤ (Less or Equal)</MenuItem>
                      <MenuItem value="<">{'<'} (Less Than)</MenuItem>
                      <MenuItem value="=">=  (Equal To)</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="Daily Rate (₦)"
                    type="number"
                    placeholder="e.g., 15000"
                    value={filters.rateValue || ''}
                    onChange={(e) => setFilters({ ...filters, rateValue: parseFloat(e.target.value) || undefined })}
                    fullWidth
                    inputProps={{ min: 0, step: 1000 }}
                    disabled={!filters.rateOperator}
                  />
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Max Vehicles"
                type="number"
                value={formData.max_vehicles}
                onChange={(e) => setFormData({ ...formData, max_vehicles: parseInt(e.target.value) || 10 })}
                fullWidth
                inputProps={{ min: 1, max: 50 }}
              />

              <TextField
                label="Display Order"
                type="number"
                value={formData.display_order}
                onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 1 })}
                fullWidth
                inputProps={{ min: 1 }}
              />
            </Box>

            <FormControl fullWidth>
              <InputLabel>Card Style</InputLabel>
              <Select
                value={formData.card_style}
                label="Card Style"
                onChange={(e) => setFormData({ ...formData, card_style: e.target.value })}
              >
                <MenuItem value="default">Default</MenuItem>
                <MenuItem value="compact">Compact</MenuItem>
                <MenuItem value="featured">Featured</MenuItem>
                <MenuItem value="minimal">Minimal</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Active (shown on dashboard)"
            />

            <Alert severity="info">
              Note: Carousels with 0 matching vehicles will be automatically deactivated
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting || !formData.title}
          >
            {submitting ? <CircularProgress size={20} /> : editingCarousel ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Carousel</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the carousel "{deletingCarousel?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CarouselManagementPage;
